import { z } from "zod";

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  role: z.string(),
  tenantId: z.string(),
  avatarUploadId: z.string().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  passwordAuthEnabled: z.boolean().optional(),
  permissions: z.array(z.string()).nullish(),
});
export type User = z.infer<typeof userSchema>;

const invitationOptionSchema = z.object({
  inviteId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  role: z.string(),
});
export type InvitationOption = z.infer<typeof invitationOptionSchema>;

export const authResponseSchema = z.object({
  accessToken: z.string().nullable().optional(),
  user: userSchema.nullable().optional(),
  mfaRequired: z.boolean().optional().default(false),
  mfaChallenge: z.string().nullable().optional(),
  mfaTrustedDevice: z.boolean().optional().default(false),
  onboardingRequired: z.boolean().optional().default(false),
  onboardingChallenge: z.string().nullable().optional(),
  onboardingEmail: z.string().nullable().optional(),
  onboardingOptions: z.array(invitationOptionSchema).optional().default([]),
  companySwitchOptions: z.array(invitationOptionSchema).optional().default([]),
});

export interface AuthSession {
  accessToken: string;
  user: User;
  companySwitchOptions: InvitationOption[];
}

export interface MfaChallenge {
  kind: "mfa";
  challenge: string;
  user: User;
  isTrustedDevice: boolean;
}

export interface OnboardingChallenge {
  kind: "onboarding";
  challenge: string;
  email: string;
  options: InvitationOption[];
}

export interface CompanySwitchPrompt {
  kind: "company-switch";
  options: InvitationOption[];
}

export type LoginResult = AuthSession | MfaChallenge | OnboardingChallenge;

export interface SignupInput {
  email: string;
  password: string;
  fullName: string;
  tenantName: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface CompleteInviteOnboardingInput {
  challenge: string;
  confirmPassword: string;
  fullName: string;
  inviteId: string;
  newPassword: string;
}

export interface GoogleCredentialInput {
  credential: string;
}

export interface GoogleSignupInput extends GoogleCredentialInput {
  tenantName: string;
}

export interface GoogleRedirectCompleteInput {
  code: string;
  tenantName?: string;
}

export type GoogleRedirectCompletion =
  | { status: "organization_required" }
  | {
      status: "authenticated";
      result: AuthSession | MfaChallenge | CompanySwitchPrompt;
    };

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

export interface MfaLoginVerifyInput {
  challenge: string;
  code?: string;
  recoveryCode?: string;
  trustDevice?: boolean;
}

export interface ProfileAvatarInput {
  fullName?: string;
  avatarUploadId?: string | null;
}

export const avatarUploadSchema = z.object({
  id: z.string(),
  mediaType: z.string(),
  byteSize: z.number().int().positive(),
});
export type AvatarUpload = z.infer<typeof avatarUploadSchema>;

export interface ProfileInput {
  email: string;
  fullName: string;
  tenantName: string;
}

export interface ChangePasswordInput {
  currentPassword?: string;
  googleCredential?: string;
  newPassword: string;
  code?: string;
  recoveryCode?: string;
}

export const mfaStatusSchema = z.object({ enabled: z.boolean() });
export const mfaPolicySchema = z.object({
  enforcementMode: z.enum(["always", "conditional"]),
  trustedDeviceDays: z.number().int().min(0).max(90),
  requireNewDevice: z.boolean(),
  requireNewBrowser: z.boolean(),
  requireNewCountry: z.boolean(),
  requireAfterPasswordChange: z.boolean(),
  requireSensitiveActions: z.boolean(),
});
export const mfaTrustedDeviceSchema = z.object({
  id: z.string(),
  deviceName: z.string(),
  userAgent: z.string().nullable(),
  countryCode: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  expiresAt: z.string(),
});
export const mfaEnrollmentSchema = z.object({ otpauthUri: z.string().url() });
export const mfaRecoveryCodesSchema = z.object({
  recoveryCodes: z.array(z.string().min(1)),
});

export type MfaStatus = z.infer<typeof mfaStatusSchema>;
export type MfaPolicy = z.infer<typeof mfaPolicySchema>;
export type MfaTrustedDevice = z.infer<typeof mfaTrustedDeviceSchema>;

export interface MfaEnrollmentInput {
  currentPassword?: string;
  googleCredential?: string;
}

export interface MfaConfirmInput {
  code: string;
}

export interface MfaDisableInput {
  currentPassword?: string;
  googleCredential?: string;
  code?: string;
  recoveryCode?: string;
}
